import { isBuildReplaceable, slugify, type CreationAsset } from '@djgg/creations';

const SOURCES = {
  build: 'Generated during a game build', bank: 'Platform fallback artwork',
  studio: 'Chosen in Assets Studio', upload: 'Uploaded by a creator',
};

/** How each stored audio container is described to a creator. Read from the
 *  stored mime, never assumed: an upload is the creator's exact PCM file, while
 *  generated music is compressed so a game bundle stays small on a phone. */
const AUDIO_FORMATS = {
  'audio/wav': 'PCM WAV',
  'audio/ogg': 'Opus (Ogg), compressed',
};

/**
 * Who is asserting the rights, and what the assertion is worth.
 * `provider-generated` is NOT a creator claim and must not be shown as one —
 * it records that a provider produced the bytes.
 */
const AUDIO_RIGHTS = {
  'creator-owned': 'Creator-owned recording. This is the uploader’s declaration, not independent legal verification.',
  licensed: 'Licensed recording. This is the uploader’s declaration, not independent legal verification.',
  'provider-generated': 'Generated with an external model or media provider. This is a provenance record, not an ownership claim on your behalf; the terms below record the generation path.',
};

/** Read only: all values come from the stored working attachment. Never
 * manufacture a license, provider, byte identity or animation reference. */
export function AssetDetails({ asset, assets }: { asset: CreationAsset; assets: readonly CreationAsset[] }) {
  const reference = asset.strip && assets.find(candidate => candidate.kind === 'sprite' && candidate.variantId === asset.strip!.referenceVariantId);
  const currentBase = asset.strip && assets.find(candidate => candidate.kind === 'sprite' && candidate.name.toLowerCase() === slugify(asset.strip!.entityName));
  return <details style={{ margin: '8px 0', overflowWrap: 'anywhere' }}>
    <summary>Details and source · {asset.name}</summary>
    <dl>
      <dt>Source</dt><dd>{asset.source ? SOURCES[asset.source] : 'Not recorded for this older asset'}</dd>
      <dt>Replacement policy</dt><dd>{isBuildReplaceable(asset)
        ? 'A future build may replace this build-managed asset. Choose or upload your own replacement to keep control of it.'
        : 'Treated as creator-owned: ordinary game builds do not replace it automatically.'}</dd>
      <dt>Original prompt</dt><dd style={{ whiteSpace: 'pre-wrap' }}>{asset.prompt || 'No generation prompt recorded (for example, an upload).'}</dd>
      {asset.strip && <>
        <dt>Animation binding</dt><dd>{asset.strip.entityName} · {asset.strip.state}</dd>
        <dt>Frame layout</dt><dd>{asset.strip.frames} frames · {asset.strip.frameWidth} × {asset.strip.frameHeight} pixels each</dd>
        <dt>Reference image</dt><dd>{reference ? `${reference.name} (currently attached)` : 'Original reference is not in the working attachment list.'}</dd>
        {currentBase && currentBase.variantId !== asset.strip.referenceVariantId && <dd role="status">The base image has changed since this animation was made. Review or regenerate this motion to match the new artwork.</dd>}
        <dt>Reference variant</dt><dd><code>{asset.strip.referenceVariantId}</code></dd>
      </>}
      {asset.audio && <>
        <dt>Audio format</dt><dd>{AUDIO_FORMATS[asset.audio.mime]} · {(asset.audio.durationMs / 1000).toFixed(2)} seconds · {asset.audio.sampleRate} Hz · {asset.audio.channels === 1 ? 'mono' : 'stereo'} · {asset.audio.bytes.toLocaleString('en-US')} bytes</dd>
        <dt>Rights</dt><dd>{AUDIO_RIGHTS[asset.audio.license.kind]}</dd>
        <dt>Recording source</dt><dd>{asset.audio.license.source}</dd>
        <dt>Rights/terms</dt><dd style={{ whiteSpace: 'pre-wrap' }}>{asset.audio.license.terms}</dd>
        {asset.kind === 'music' && <><dt>Music loop</dt><dd>{asset.audio.loopStartMs !== undefined && asset.audio.loopEndMs !== undefined
          ? `${(asset.audio.loopStartMs / 1000).toFixed(2)}–${(asset.audio.loopEndMs / 1000).toFixed(2)} seconds${
            asset.audio.license.kind === 'provider-generated'
              ? '. The end of this loop is cross-faded into its start, so it repeats without a click — it is not a seamless musical loop, and you may hear the phrase restart. The provider delivered this track as a lossy MP3 and it was re-encoded to Opus here: two lossy encodings, so expect slightly less fidelity than the provider’s own file.'
              : ''}`
          : 'No custom loop range recorded'}</dd></>}
      </>}
      <dt>Stored variant</dt><dd><code>{asset.variantId}</code></dd>
      <dt>Exact-byte identity (SHA-256)</dt><dd>{asset.contentSha256
        ? <code>{asset.contentSha256}</code>
        : 'Not recorded. A name or URL is not proof of identical media bytes.'}</dd>
    </dl>
    <p className="muted">These are working-copy details. Published versions keep their own media until you explicitly publish a new version.</p>
  </details>;
}
